import React, { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";

import { Disclosure } from "@headlessui/react";
import { SvgCalendar, Caret } from "@routes/IconeSvg";
import { useFormContext, Controller } from "react-hook-form";

import DefaultDatePicker from "@components/arhDatePicker/DefaultDatePicker";
import {
  ListboxSimple,
  CheckboxSimple,
  Input
} from "@components/tailwindComponents";

import { convertToNumericString } from "@helpers/_functions";
import { TaskStatus } from "@helpers/moduleConstants/taskConstants";

export const DateType = {
  FIXED_DATE: "FIXED_DATE",
  FOLLOWING_TASK: "FOLLOWING_TASK"
};

export default function DateManagement({ taskList = [], selectedTask = null }) {
  const { t: translate } = useTranslation("beginEnd.protocol");

  const { control, watch } = useFormContext();

  const availableTasks = useMemo(() => {
    return removeDescendants(taskList, selectedTask?._id);
  }, [taskList, selectedTask]);

  const [dateType, status] = watch(["dateType", "status"]);

  const requiredRule = useCallback(
    (correspondingDateType) => ({
      validate: {
        required: (value) => {
          if (correspondingDateType !== dateType) {
            return true;
          }

          if (status === TaskStatus.NOT_APPLICABLE) {
            return true;
          }

          return !!value;
        }
      }
    }),
    [dateType, status]
  );

  return (
    <div className="arh-w-full ">
      <Disclosure>
        {({ open }) => (
          <>
            <Disclosure.Button
              className={`arh-mt-2 arh-flex arh-w-full arh-items-center arh-justify-between arh-rounded-md arh-border arh-border-solid arh-border-greyBorderInput arh-bg-transparent arh-p-4 ${
                open && "arh-rounded-bl-none arh-rounded-br-none arh-border-b-0"
              }`}
            >
              <div className="arh-flex arh-items-center arh-space-x-3">
                <span>{SvgCalendar()}</span>
                <span className="arh-font-poppins arh-text-xs arh-font-medium arh-text-darkColor">
                  {translate("Dates prévue et limite")}
                </span>
              </div>
              <span>
                <Caret
                  className={`${open ? "arh-rotate-90" : "arh-rotate-0"}`}
                  aria-hidden="true"
                />
              </span>
            </Disclosure.Button>

            <Disclosure.Panel
              className={`input_file_theme_employer  arh-rounded-md arh-border arh-border-solid arh-border-greyBorderInput arh-p-4 ${
                open &&
                "arh-border-translate-0 arh-rounded-tl-none arh-rounded-tr-none"
              }`}
            >
              <div className="arh-w-full ">
                <div className="arh-w-full ">
                  <Controller
                    control={control}
                    name="dateType"
                    defaultValue={DateType.FIXED_DATE}
                    render={({ field: { value, onChange } }) => (
                      <CheckboxSimple
                        label={translate("Dates fixes")}
                        checked={value === DateType.FIXED_DATE}
                        onClick={() => onChange(DateType.FIXED_DATE)}
                        className="arh-mb-[1rem] !arh-p-0 "
                        fontLabel="arh-font-bold arh-text-darkColor arh-text-xs "
                        customClass="arh-ml-[1rem] "
                      />
                    )}
                  />
                  <div className="arh-mb-[0.938rem] arh-flex arh-w-full arh-items-center arh-justify-between">
                    <div className="employerThemeDatePicker blackSvgColor calendar_with_btn datepicker_vacation arh-relative arh-flex-[0.5_0.5_0%]">
                      <Controller
                        control={control}
                        name="predictedDate"
                        rules={requiredRule(DateType.FIXED_DATE)}
                        defaultValue=""
                        render={({
                          field: { onChange, value },
                          fieldState: { invalid }
                        }) => (
                          <>
                            <label
                              className={`${invalid ? "arh-text-invalidColorTextInput" : "arh-text-black"} arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium`}
                            >
                              {translate("Date prévue")}
                            </label>
                            <DefaultDatePicker
                              className="datepickerDropdown GreenThemeSelect"
                              // name="date_predicted"
                              placeholder={translate("Date prévue")}
                              dateValue={value}
                              setDateValue={onChange}
                              classPosition="calendar_left arh-flex arh-justify-center"
                              //invalid={invalid}
                              isInForeground
                              customClass="arh-bottom-12"
                              marginTop={0}
                              invalid={invalid}
                            />
                          </>
                        )}
                      />
                    </div>

                    <div className="employerThemeDatePicker blackSvgColor calendar_with_btn datepicker_vacation arh-relative arh-flex-[0.5_0.5_0%] last:arh-ml-[1.25rem]">
                      <label className="arh-mb-1 arh-flex arh-w-full arh-font-poppins arh-text-xs arh-font-medium arh-text-black">
                        {translate("Date limite")}
                      </label>
                      <Controller
                        control={control}
                        name="deadlineDate"
                        defaultValue=""
                        render={({ field: { onChange, value } }) => (
                          <DefaultDatePicker
                            className="datepickerDropdown GreenThemeSelect"
                            // name="deadline"
                            placeholder={translate("Date limite")}
                            dateValue={value}
                            setDateValue={onChange}
                            classPosition="calendar_right arh-flex arh-justify-center"
                            isInForeground
                            customClass="arh-bottom-12"
                            marginTop={0}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                <div className="arh-mt-[2rem] arh-w-full ">
                  <Controller
                    control={control}
                    name="dateType"
                    render={({ field: { value, onChange } }) => (
                      <CheckboxSimple
                        label={translate("À la suite d'une autre tache")}
                        checked={value === DateType.FOLLOWING_TASK}
                        onClick={() => onChange(DateType.FOLLOWING_TASK)}
                        className="arh-mb-[1rem] !arh-p-0 "
                        fontLabel="arh-font-bold arh-text-darkColor arh-text-xs "
                        customClass="arh-ml-[1rem] "
                      />
                    )}
                  />

                  <div className="arh-border-translate-0 arh-mt-[1rem] arh-w-full arh-border-x-0 arh-border-b arh-border-solid arh-border-[#ECECEC] arh-pb-[0.5rem] ">
                    <label className="arh-mb-[0.5rem] arh-w-full arh-text-left arh-font-poppins arh-text-xs arh-font-medium arh-text-darkColor ">
                      {translate("Date prévue")}
                    </label>
                    <div className="arh-flex arh-w-full arh-items-center ">
                      <Controller
                        control={control}
                        name="predictedDateTriggerInterval"
                        defaultValue={"1"}
                        rules={requiredRule(DateType.FOLLOWING_TASK)}
                        render={({
                          field: { value, name, onChange },
                          fieldState: { invalid }
                        }) => (
                          <div className="arh-mb-1 arh-flex arh-flex-col">
                            <Input
                              value={value}
                              name={name}
                              label={translate("Jours")}
                              placeholder={translate("")}
                              onChange={(e) => {
                                onChange(
                                  convertToNumericString(e.target.value)
                                );
                              }}
                              additionnalClas="!arh-w-[5rem] "
                              invalid={invalid}
                            />
                          </div>
                        )}
                      />
                      &nbsp;&nbsp;
                      <span className="-arh-mb-[1.5rem] arh-font-poppins arh-text-xs arh-font-medium arh-text-[#8B8F91] ">
                        {translate("Après")}
                      </span>
                      &nbsp;&nbsp;
                      <Controller
                        control={control}
                        name="predictedDateTriggerTask"
                        defaultValue={null}
                        rules={requiredRule(DateType.FOLLOWING_TASK)}
                        render={({
                          field: { onChange, value, name },
                          fieldState: { invalid }
                        }) => (
                          <ListboxSimple
                            placeholder={translate("Tâche")}
                            name={name}
                            label={translate("Tâche")}
                            optionList={availableTasks}
                            optionValueAttibut="_id"
                            optionTextAttribut="name"
                            selectedValue={value}
                            setSelectedValue={(task) => {
                              onChange(task);
                            }}
                            noRadio={true}
                            withTooltip={true}
                            invalid={invalid}
                          />
                        )}
                      />
                    </div>
                  </div>

                  <div className="arh-mt-[1rem] arh-w-full ">
                    <label className="arh-mb-[0.5rem] arh-w-full arh-text-left arh-font-poppins arh-text-xs arh-font-medium arh-text-darkColor ">
                      {translate("Date limite")}
                    </label>
                    <div className="arh-flex arh-w-full arh-items-center ">
                      <Controller
                        control={control}
                        name="deadlineDateTriggerInterval"
                        defaultValue={"1"}
                        rules={requiredRule(DateType.FOLLOWING_TASK)}
                        render={({
                          field: { value, name, onChange },
                          fieldState: { invalid }
                        }) => (
                          <div className="arh-mb-1 arh-flex arh-flex-col">
                            <Input
                              value={value}
                              name={name}
                              label={translate("Jours")}
                              placeholder={translate("")}
                              onChange={(e) => {
                                onChange(
                                  convertToNumericString(e.target.value)
                                );
                              }}
                              additionnalClas="!arh-w-[5rem] "
                              invalid={invalid}
                            />
                          </div>
                        )}
                      />
                      &nbsp;&nbsp;
                      <span className="-arh-mb-[1.5rem] arh-font-poppins arh-text-xs arh-font-medium arh-text-[#8B8F91] ">
                        {translate("Après")}
                      </span>
                      &nbsp;&nbsp;
                      <Controller
                        control={control}
                        name="deadlineDateTriggerTask"
                        defaultValue={null}
                        rules={requiredRule(DateType.FOLLOWING_TASK)}
                        render={({
                          field: { onChange, value, name },
                          fieldState: { invalid }
                        }) => (
                          <ListboxSimple
                            placeholder={translate("Tâche")}
                            name={name}
                            label={translate("Tâche")}
                            optionList={availableTasks}
                            optionValueAttibut="_id"
                            optionTextAttribut="name"
                            selectedValue={value}
                            setSelectedValue={(task) => {
                              onChange(task);
                            }}
                            noRadio={true}
                            withTooltip={true}
                            invalid={invalid}
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </div>
  );
}

function getDescendants(taskList, taskId) {
  const children = taskList.filter(
    (t) =>
      t.predictedDateTriggerTask === taskId ||
      t.deadlineDateTriggerTask === taskId
  );

  let result = [];
  for (const child of children) {
    result.push(child._id);

    result = result.concat(getDescendants(taskList, child._id));
  }

  return result;
}

function removeDescendants(taskList, selectedTaskId) {
  if (!selectedTaskId) {
    return taskList;
  }

  const descendants = getDescendants(taskList, selectedTaskId);
  const excludedIds = new Set([selectedTaskId, ...descendants]);

  return taskList.filter(
    (t) => !excludedIds.has(t._id) && t.status === TaskStatus.TODO
  );
}
